/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import { LoaderCircle } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { useLocale } from '@/components/LocaleContext'
import { clearStoredAuthKey, getAuthCookie } from '@/lib/auth'
import { getMediaUrl } from '@/lib/media'
import type { Localization, Topbar } from '@/payload-types'
import { CART_UPDATED_EVENT } from '@/utilities/eventEmitter'
import { resolveTopbarNavLabel } from '@/utilities/navLinkLabel'
import { getLocalizedTranslationData } from '@/utilities/translationsData'
import { ConfirmDialog } from '../ConfirmDialog'
import { HeaderMobileMenu } from './HeaderMobileMenu'

import './Header.css'

type HeaderProps = {
  topbar: Topbar | null
}

const DESKTOP_BREAKPOINT = 1369
const GUEST_CART_COUNT_SNAPSHOT_KEY = 'gallium_guest_cart_count_snapshot_v1'

const messages = {
  logout: {
    en: 'Logout',
    fr: 'Se déconnecter',
    ar: 'تسجيل الخروج',
  },
  logoutConfirmation: {
    en: 'Are you sure you want to logout?',
    fr: 'Êtes-vous sûr de vouloir vous déconnecter ?',
    ar: 'هل أنت متأكد أنك تريد تسجيل الخروج؟',
  },
  yes: {
    en: 'Yes',
    fr: 'Oui',
    ar: 'نعم',
  },
  no: {
    en: 'No',
    fr: 'Non',
    ar: 'لا',
  },
  logoutSuccessful: {
    en: 'Logout successful',
    fr: 'Déconnexion réussie',
    ar: 'تم تسجيل الخروج بنجاح',
  },
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`)
    const update = () => setIsDesktop(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  return isDesktop
}

export function Header({ topbar }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false)
  const [count, setCount] = useState(0)
  const [cmsPageTitle, setCmsPageTitle] = useState<string | null>(null)
  const [logoutOpen, setLogoutOpen] = useState(false)

  const accountDropdownRef = useRef<HTMLDivElement>(null)
  const pathname = usePathname() ?? ''
  const isDesktop = useIsDesktop()
  const isLoggedIn = Boolean(getAuthCookie())
  const { locale, translatedData, applyLocaleChange, setLocaleLoading, localeLoading } = useLocale()

  function commonT(key: keyof Localization['common'], enFallback: string): string {
    const c = translatedData?.common?.[key]
    if (c) return c
    const s = getLocalizedTranslationData((locale as AppLocale) ?? 'en').common?.[key]
    if (s) return s
    return enFallback
  }

  const logoUrl = topbar ? getMediaUrl(topbar.logo) : null
  const imageUrl = topbar ? getMediaUrl(topbar.image) : null
  const navLinks = topbarNavToLinks(topbar?.navLinks ?? null)

  function topbarNavToLinks(
    navLinks: Topbar['navLinks'],
  ): { id: string; label: string; href: string }[] {
    if (!navLinks?.length)
      return [
        { label: translatedData?.navbar?.home ?? 'Home', href: '/' },
        { label: translatedData?.navbar?.products ?? 'Products', href: '/products' },
        { label: translatedData?.navbar?.news ?? 'Insights', href: '/insights' },
        { label: translatedData?.navbar?.contact ?? 'Contact', href: '/contact' },
      ].map((l, i) => ({ ...l, id: `default-${i}` }))
    return navLinks.map((link) => {
      return {
        id: link.id ?? `${link.navKey ?? link.category}-${link.url}`,
        label: resolveTopbarNavLabel(link, translatedData),
        href: link.url ?? '#',
      }
    })
  }

  function getPageHero(pathname: string) {
    if (pathname === '/about') {
      return {
        title: translatedData?.hero.aboutTitle ?? 'ABOUT GALLIUM',
        breadcrumb: translatedData?.hero.aboutBreadcrumb ?? 'HOME/ABOUT GALLIUM',
      }
    }

    if (pathname === '/contact') {
      return {
        title: translatedData?.hero.contactTitle ?? 'CONTACT US',
        breadcrumb: translatedData?.hero.contactBreadcrumb ?? 'HOME/CONTACT US',
      }
    }

    if (pathname.startsWith('/products')) {
      return {
        title: translatedData?.hero.productsTitle ?? 'PRODUCTS',
        breadcrumb: translatedData?.hero.productsBreadcrumb ?? 'HOME/PRODUCTS',
      }
    }

    if (pathname.startsWith('/insights')) {
      return {
        title: translatedData?.hero.insightsTitle ?? 'INSIGHTS',
        breadcrumb: translatedData?.hero.insightsBreadcrumb ?? 'HOME/INSIGHTS',
      }
    }

    if (pathname.startsWith('/enquiry-cart')) {
      return {
        title: translatedData?.hero.enquiryTitle ?? 'ENQUIRY',
        breadcrumb: translatedData?.hero.enquiryBreadcrumb ?? 'HOME/ENQUIRY',
      }
    }
    if (pathname.startsWith('/dealer-cart')) {
      return {
        title: translatedData?.hero.dealerTitle ?? 'DEALER',
        breadcrumb: translatedData?.hero.dealerBreadcrumb ?? 'HOME/DEALER',
      }
    }
    if (pathname.startsWith('/privacy-policy')) {
      return {
        title: translatedData?.hero.privacyTitle ?? 'PRIVACY POLICY',
        breadcrumb: translatedData?.hero.privacyBreadcrumb ?? 'HOME/PRIVACY POLICY',
      }
    }
    if (pathname.startsWith('/terms-and-conditions')) {
      return {
        title: translatedData?.hero.tandcTitle ?? 'TERMS AND CONDITIONS',
        breadcrumb: translatedData?.hero.tandcBreadcrumb ?? 'HOME/TERMS AND CONDITIONS',
      }
    }

    return null
  }

  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLocaleLoading(true)
    const newLocale = e.target.value as AppLocale
    if (newLocale === locale) return
    applyLocaleChange(newLocale)
  }

  const refreshCartCount = async () => {
    try {
      const res = await fetch('/api/cart?count=1', {
        cache: 'no-store',
      })
      const data = await res.json()
      const nextCount = Number(data.count ?? 0)
      setCount(nextCount)
      if (!isLoggedIn) {
        localStorage.setItem(GUEST_CART_COUNT_SNAPSHOT_KEY, String(nextCount))
      }
    } catch {
      if (!isLoggedIn) {
        const fallback = Number(localStorage.getItem(GUEST_CART_COUNT_SNAPSHOT_KEY) ?? '0')
        setCount(Number.isFinite(fallback) ? fallback : 0)
      }
    }
  }
  /* Lock body scroll when mobile menu is open so content stays in fixed position */
  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
    } else {
      const scrollY = document.body.style.top ? Math.abs(parseInt(document.body.style.top, 10)) : 0
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      if (scrollY) window.scrollTo(0, scrollY)
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    if (!accountDropdownOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target as Node)) {
        setAccountDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [accountDropdownOpen])

  useEffect(() => {
    const segments = pathname.split('/').filter(Boolean)
    const slug = segments[0]
    if (!slug || segments.length > 1) {
      setCmsPageTitle(null)
      return
    }
    fetch(`/api/page-title?slug=${encodeURIComponent(slug)}&locale=${encodeURIComponent(locale)}`)
      .then((r) => r.json())
      .then((data) => setCmsPageTitle(data?.title ?? null))
      .catch(() => setCmsPageTitle(null))
  }, [pathname, locale])

  useEffect(() => {
    if (!isLoggedIn) {
      const initial = Number(localStorage.getItem(GUEST_CART_COUNT_SNAPSHOT_KEY) ?? '0')
      setCount(Number.isFinite(initial) ? initial : 0)
    }

    refreshCartCount()

    const handler = () => {
      refreshCartCount()
    }

    window.addEventListener(CART_UPDATED_EVENT, handler)
    return () => window.removeEventListener(CART_UPDATED_EVENT, handler)
  }, [isLoggedIn])

  const isHome = pathname === '/'
  const pageHero = getPageHero(pathname)
  const heroTitle = isHome
    ? null
    : cmsPageTitle
      ? cmsPageTitle?.toUpperCase()
      : pageHero
        ? pageHero.title
        : (topbar?.heroTitle ?? null)
  const heroBreadcrumb = isHome
    ? null
    : cmsPageTitle
      ? `${translatedData?.common.home ?? 'HOME'}/${cmsPageTitle.toUpperCase()}`
      : pageHero
        ? pageHero.breadcrumb
        : (topbar?.heroBreadcrumb ?? null)
  const showHero = Boolean(heroTitle ?? heroBreadcrumb)

  /* On home: transparent header (no white bar), no hero title/breadcrumb */
  const headerClassName = [
    'gallium-header',
    !showHero ? 'gallium-header--no-hero' : '',
    isHome ? 'gallium-header--home' : '',
    mobileMenuOpen ? 'gallium-header--mobile-menu-open' : '',
    accountDropdownOpen && isDesktop ? 'gallium-header--account-dropdown-open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <header className={headerClassName} dir={locale == 'ar' ? 'rtl' : 'ltr'}>
      <div className="gallium-header__banner">
        {imageUrl && !isHome && (
          <div className="gallium-header__image-wrap">
            <Image
              src={imageUrl}
              alt=""
              fill
              className="gallium-header__image"
              sizes="100vw"
              priority
            />
          </div>
        )}
        <div className="gallium-header__row ">
          <Link href="/" className="gallium-header__logo" aria-label="Gallium home">
            {logoUrl ? (
              <Image
                src={logoUrl}
                alt="Gallium – Inspired by filtration"
                fill
                priority
                className="gallium-header__logo-img"
              />
            ) : (
              <Image
                src="/gallium-transparent.png"
                alt="Gallium – Inspired by filtration"
                fill
                priority
                className="gallium-header__logo-img"
              />
            )}
          </Link>

          <div className="gallium-header__nav-wrap">
            <nav aria-label="Main">
              <ul className="gallium-header__nav">
                {navLinks.map(({ id, label, href }) => (
                  <li key={id}>
                    <Link
                      href={href}
                      className="gallium-header__nav-link  text-left rtl:text-right"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          <div className="gallium-header__utils ">
            <div className="gallium-header__lang">
              {localeLoading ? <LoaderCircle className="animate-spin" /> : <></>}

              <select
                value={locale}
                onChange={handleLanguageChange}
                className="gallium-header__lang-select pl-3 pr-2 py-2 bg-transparent text-white mr-2 [&>option]:text-black [&>option]:bg-white"
                aria-label={translatedData?.common.language ?? 'Language'}
              >
                <option value="en">{isDesktop ? 'English' : 'EN'}</option>
                <option value="fr">{isDesktop ? 'Français' : 'FR'}</option>
                <option value="ar">{isDesktop ? 'عربي' : 'AR'}</option>
              </select>
            </div>

            <div ref={accountDropdownRef} className="gallium-header__account-dropdown-wrap">
              <button
                type="button"
                className="gallium-header__icon-btn gallium-header__menu-btn"
                aria-label={
                  isDesktop
                    ? accountDropdownOpen
                      ? (translatedData?.common.close_account_menu ?? 'Close account menu')
                      : (translatedData?.common.open_account_menu ?? 'Open account menu')
                    : mobileMenuOpen
                      ? (translatedData?.common.close_menu ?? 'Close menu')
                      : (translatedData?.common.open_menu ?? 'Open menu')
                }
                aria-expanded={isDesktop ? accountDropdownOpen : mobileMenuOpen}
                aria-haspopup={isDesktop ? 'menu' : undefined}
                onClick={() => {
                  if (isDesktop) {
                    setAccountDropdownOpen((prev) => !prev)
                  } else {
                    setMobileMenuOpen((prev) => !prev)
                  }
                }}
              >
                {/* Inline SVG so hamburger is always visible (no image load) */}
                <svg
                  className="gallium-header__menu-btn-icon"
                  width="34"
                  height="23"
                  viewBox="0 0 34 23"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path
                    d="M24 23H4V20H24V23ZM24 13H11V10H24V13ZM24 3H0V0H24V3Z"
                    fill="currentColor"
                  />
                  {count > 0 && <circle cy="4" cx="30" r="4" fill="red" />}
                </svg>
              </button>

              {isDesktop && accountDropdownOpen && (
                <div className="gallium-header__account-dropdown" role="menu">
                  <Link
                    href={isLoggedIn ? '/dealer-cart' : '/enquiry-cart'}
                    className="gallium-header__account-dropdown-link text-left rtl:text-right"
                    role="menuitem"
                    onClick={() => setAccountDropdownOpen(false)}
                  >
                    {commonT('cart', 'Cart')} {count > 0 && `(${count})`}
                  </Link>
                  {isLoggedIn && (
                    <Link
                      href="/dashboard"
                      className="gallium-header__account-dropdown-link text-left rtl:text-right"
                      role="menuitem"
                      onClick={() => setAccountDropdownOpen(false)}
                    >
                      {commonT('dashboard', 'Dashboard')}
                    </Link>
                  )}
                  {isLoggedIn ? (
                    <button
                      type="button"
                      className="gallium-header__account-dropdown-btn text-left rtl:text-right"
                      role="menuitem"
                      onClick={() => setLogoutOpen(true)}
                    >
                      {commonT('logout', 'Logout')}
                    </button>
                  ) : (
                    <Link
                      href="/login"
                      className="gallium-header__account-dropdown-link text-left rtl:text-right"
                      role="menuitem"
                      onClick={() => setAccountDropdownOpen(false)}
                    >
                      {commonT('login', 'Login')}
                    </Link>
                  )}
                </div>
              )}
            </div>

            <HeaderMobileMenu
              navLinks={navLinks}
              open={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              handleLogout={() => setLogoutOpen(true)}
              count={count}
            />
          </div>
        </div>

        {showHero && (
          <div className="gallium-header__hero">
            {heroTitle && (
              <h1
                className={`gallium-header__hero-title${pathname === '/enquiry-cart' ? ' !text-[#FF0000]' : ''}`}
              >
                {heroTitle}
              </h1>
            )}
            {(heroTitle || heroBreadcrumb) && <hr className="gallium-header__hero-line" />}
            {heroBreadcrumb && (
              <nav
                aria-label="Breadcrumb"
                className="gallium-header__hero-breadcrumb gallium-header__hero-breadcrumb-nav"
              >
                {(() => {
                  const segments = heroBreadcrumb
                    .split('/')
                    .map((s) => s.trim())
                    .filter(Boolean)
                  if (segments.length === 0) return null
                  return (
                    <p className="gallium-header__hero-breadcrumb">
                      {segments.map((segment, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && (
                            <span className="gallium-header__breadcrumb-sep" aria-hidden>
                              /
                            </span>
                          )}
                          {i === 0 &&
                          segment.toUpperCase() === translatedData?.common.home?.toUpperCase() ? (
                            <Link
                              href="/"
                              className="gallium-header__breadcrumb-link"
                              aria-current={pathname === '/' ? 'page' : undefined}
                            >
                              {segment}
                            </Link>
                          ) : (
                            <span>{segment}</span>
                          )}
                        </React.Fragment>
                      ))}
                    </p>
                  )
                })()}
              </nav>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog
        dir={locale == 'ar' ? 'rtl' : 'ltr'}
        isOpen={logoutOpen}
        title={messages.logout[locale]}
        message={messages.logoutConfirmation[locale]}
        yesLabel={messages.yes[locale]}
        noLabel={messages.no[locale]}
        onConfirm={() => {
          toast.loading(messages.logoutSuccessful[locale])
          setTimeout(() => {
            clearStoredAuthKey()
            window.location.replace('/')
          }, 2000)
        }}
        onCancel={() => setLogoutOpen(false)}
      />
    </header>
  )
}
